import { useState, useEffect, useCallback, useRef } from "react";
import {
  logError,
  trackTransferInitiated,
  trackTransferCompleted,
  trackTransferFailed,
  trackTransferAbandoned,
} from "../analytics.ts";
import { useSignMessage } from "wagmi";
import {
  EspectroAccountManager,
  type TransferResult,
  type EVMTransactionRequest,
} from "../lit/account.ts";
import {
  type TransferStage,
  type EspectroOperation,
  type TransferEvidence,
  loadOperation,
  clearOperation,
  abandonOperation,
  isTerminalStage,
  hasChainCommitment,
} from "../lit/operation.ts";

export interface UseTransferReturn {
  stage: TransferStage | null;
  error: string | null;
  hasPendingTransfer: boolean;
  isProcessing: boolean;
  evidence: TransferEvidence | null;

  start: (params: {
    sourceAsset: string;
    sourceAddress: string;
    amount: string;
    destinationAsset: string;
    destinationAddress: string;
    signAndSend: (tx: EVMTransactionRequest) => Promise<string>;
  }) => Promise<TransferResult>;

  retry: (signAndSend: (tx: EVMTransactionRequest) => Promise<string>) => Promise<void>;

  reset: (force?: boolean) => void;

  pendingEvidence: TransferEvidence | null;
}

export function useTransfer(): UseTransferReturn {
  const managerRef = useRef(new EspectroAccountManager());
  const { signMessageAsync } = useSignMessage();

  const [stage, setStage] = useState<TransferStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingOp, setPendingOp] = useState<EspectroOperation | null>(null);
  const [evidence, setEvidence] = useState<TransferEvidence | null>(null);

  const updateStage = useCallback((newStage: TransferStage) => {
    const op = loadOperation();
    if (op?.type === "transfer") {
      setEvidence(op.evidence as TransferEvidence);
    }
    setStage(newStage);
  }, []);

  useEffect(() => {
    managerRef.current.loadAccount();
    const op = loadOperation();
    if (op && op.type === "transfer" && !isTerminalStage(op.stage)) {
      setPendingOp(op);
      setEvidence(op.evidence as TransferEvidence);
      // Do NOT set stage here — stage drives the in-progress UI and should only
      // be set when the transfer coroutine is actively running. On page load we
      // want the "Resume Transfer" prompt, not a frozen progress view.
    } else if (op && op.type === "transfer" && op.stage === "failed") {
      setPendingOp(op);
      setStage("failed");
      setEvidence(op.evidence as TransferEvidence);
      setError(op.error ?? "Transfer failed");
    }
  }, []);

  const hasPendingTransfer = pendingOp !== null;
  const isProcessing = stage !== null && stage !== "completed" && stage !== "failed";

  const start = useCallback(
    async (params: {
      sourceAsset: string;
      sourceAddress: string;
      amount: string;
      destinationAsset: string;
      destinationAddress: string;
      signAndSend: (tx: EVMTransactionRequest) => Promise<string>;
    }): Promise<TransferResult> => {
      setError(null);
      setStage("initiating");

      trackTransferInitiated({ asset: params.sourceAsset, network: params.sourceAsset.split(".")[0] ?? params.sourceAsset });

      try {
        const result = await managerRef.current.transfer({
          ...params,
          signMessage: (msg) => signMessageAsync({ message: msg }),
          onProgress: updateStage,
        });
        setStage("completed");
        setPendingOp(null);
        trackTransferCompleted({ asset: params.sourceAsset, network: params.sourceAsset.split(".")[0] ?? params.sourceAsset });
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Transfer failed";
        setError(msg);
        const op = loadOperation();
        if (op) setPendingOp(op);
        if (op?.stage === "failed") setStage("failed");
        trackTransferFailed({ asset: params.sourceAsset, network: params.sourceAsset.split(".")[0] ?? params.sourceAsset, reason: msg });
        logError(err, { asset: params.sourceAsset, stage: op?.stage ?? "initiating" });
        throw err;
      }
    },
    [],
  );

  const retry = useCallback(
    async (signAndSend: (tx: EVMTransactionRequest) => Promise<string>) => {
      const op = loadOperation();
      if (!op || op.type !== "transfer" || (isTerminalStage(op.stage) && op.stage !== "failed")) {
        throw new Error("No resumable transfer operation");
      }

      const ev = op.evidence as TransferEvidence;
      setError(null);

      if (op.stage === "failed") {
        op.stage = findLastGoodStage(op);
        op.error = undefined;
        const { saveOperation: save } = await import("../lit/operation.ts");
        save(op);
      }

      setStage(op.stage as TransferStage);

      try {
        await managerRef.current.transfer({
          sourceAsset: ev.sourceAsset!,
          sourceAddress: ev.sourceAddress!,
          amount: ev.requestedAmount!,
          destinationAsset: ev.destinationAsset!,
          destinationAddress: ev.destinationAddress!,
          signAndSend,
          signMessage: (msg) => signMessageAsync({ message: msg }),
          onProgress: updateStage,
        });
        setStage("completed");
        setPendingOp(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Retry failed";
        setError(msg);
        const refreshedOp = loadOperation();
        if (refreshedOp) setPendingOp(refreshedOp);
        if (refreshedOp?.stage === "failed") setStage("failed");
      }
    },
    [],
  );

  const reset = useCallback((force = false) => {
    const op = loadOperation();
    if (op && hasChainCommitment(op) && !force) {
      console.warn("[useTransfer] reset blocked: chain commitment exists. Pass force=true to override.");
      return;
    }

    // Never auto-wipe the account on a failed transfer — funds may be sitting
    // at the transparent address and the user needs recovery access.
    const isSafeToWipe = force || !op || op.type !== "transfer" || op.stage === "completed";

    if (op && op.type === "transfer") {
      const ev = op.evidence as TransferEvidence;
      trackTransferAbandoned({
        asset: ev.sourceAsset ?? "unknown",
        network: ev.sourceAsset?.split(".")[0] ?? "unknown",
        step: stage !== null ? "review" : "configure",
      });
    }
    if (op) abandonOperation(op);
    if (isSafeToWipe) {
      if (force) {
        managerRef.current.forceWipe();
      } else {
        managerRef.current.safeWipe();
      }
    }
    clearOperation();

    setStage(null);
    setError(null);
    setPendingOp(null);
    setEvidence(null);
  }, []);

  const pendingEvidence = pendingOp
    ? (pendingOp.evidence as TransferEvidence)
    : null;

  return {
    stage,
    error,
    hasPendingTransfer,
    isProcessing,
    evidence,
    start,
    retry,
    reset,
    pendingEvidence,
  };
}

function findLastGoodStage(op: EspectroOperation): TransferStage {
  const ev = op.evidence as TransferEvidence;

  if (ev.exitSwapId && ev.unshieldTxId) return "exit_confirming";
  if (ev.unshieldRawTx) return "unshielding";
  if (ev.shieldTxId) return "exit_preparing";
  if (ev.shieldedAmountZats) return "shield_confirming";
  if (ev.depositUtxos?.length) return "shielding";
  if (ev.enterSwapTxHash) return "deposit_arriving";
  if (ev.enterSwapId) return "swap_signing";

  return "initiating";
}
